import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Badge, Button, Card, ConfirmDialog, EmptyState, IconMore, IconSearch, LoadingList, MenuButton, PageHeader, SelectionBar, Table, Td, Th,
    chipClass, cx, fieldClass, filterLabelClass, useIndicator, useMedia,
} from '@timely/ui';
import {
    CHEQUE_BULK_LIMIT, CHEQUE_DATE_PRESETS, CHEQUE_STATES, CHEQUE_STATE_ORDER, ChequeStatus, chequeActions,
    chequeDatePresetOf, chequeDateRange, holds, isChequeState, type ChequeState,
} from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { useFormatters } from '../book/useFormatters';
import { DateField } from '../forms/DateField';
import { ChequeDialog } from '../cheques/ChequeDialog';
import {
    DEFAULT_CHEQUE_FILTERS, changeChequeStatus, customerById, deleteCheque, matchingChequeIds, stateOf,
    useChequeRegister, type ChequeFilters, type ChequeRow, type ChequeTotals,
} from '../cheques/useCheques';
import { BULK_MOVES, ChequeBulkDialog } from '../cheques/ChequeBulkDialog';
import { useMemberNames } from '../book/useBook';
import { paths } from '../workspace/navigation';

/**
 * The cheque register.
 *
 * One list of every cheque, the six states above it as both the overview and
 * the filter, a filter bar, and the actions for a row on the row. That shape is
 * the reference product's, arrived at after a three-tab redesign that
 * scattered the same cheques across "needs attention", "coming up" and
 * "finished" and left people hunting for one they had just seen.
 *
 * Two rules it keeps:
 *
 *   **A state counts exactly what pressing it opens.** Both come from
 *   `cheque_totals()`, which counts each state with every *other* filter
 *   applied — so "Bounced 3" next to a bank filter means three at that bank,
 *   and pressing it shows those three.
 *
 *   **A row carries only the actions its state has.** A cleared cheque has one
 *   quiet Undo and no Clear; offering Clear on it is how somebody clears it
 *   twice and wonders why the total moved. The list comes from
 *   `chequeActions()`, and the database refuses the transition as well —
 *   a hidden button is not an enforcement.
 */
export function Cheques() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const { money, count, date, today } = useFormatters();
    // The register's table needs about 1000px of its own (five fixed columns and the actions); narrower,
    // on a phone, a tablet or a small laptop beside the sidebar, each cheque is a card with the same facts.
    const asCards = useMedia(CARDS_QUERY);

    const canManage = holds(context, 'cheques.manage');
    // Whose cheques is a question for somebody who sees more than their own.
    const wholeBook = holds(context, 'customers.view.all');
    const names = useMemberNames(workspace.id);

    const [params, setParams] = useSearchParams();
    const filters = filtersFromParams(params);
    const customerName = useCustomerName(workspace.id, filters.customer);

    const { rows, totals, banks, loading, loadingMore, error, hasMore, reload, loadMore } =
        useChequeRegister(workspace.id, filters);

    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [editing, setEditing] = useState<ChequeRow | 'new' | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<ChequeRow | null>(null);

    // Chosen cheques belong to the question they were chosen from.
    const question = params.toString();
    const [chosen, setChosen] = useState<Set<string>>(() => new Set());
    useEffect(() => { setChosen(new Set()); }, [question]);
    const [moving, setMoving] = useState<{ status: ChequeStatus; ids: string[] } | null>(null);
    const [choosingAll, setChoosingAll] = useState(false);
    const toggle = (id: string, on: boolean) => setChosen(current => {
        const next = new Set(current);
        if (on) next.add(id); else next.delete(id);
        return next;
    });
    const chooseAll = async () => {
        setChoosingAll(true);
        try {
            setChosen(new Set(await matchingChequeIds(workspace.id, filters, CHEQUE_BULK_LIMIT)));
        } catch (failed) {
            setNotice({ tone: 'error', text: failed instanceof Error ? failed.message : 'That did not work.' });
        } finally {
            setChoosingAll(false);
        }
    };

    const set = (changes: Partial<ChequeFilters>) => {
        const next = new URLSearchParams(params);
        for (const [key, value] of Object.entries(changes)) {
            const isDefault = value === '' || value === 'any' || value === undefined;
            if (isDefault) next.delete(key); else next.set(key, String(value));
        }
        setParams(next, { replace: true });
    };

    const narrowed =
        (filters.state !== 'any' ? 1 : 0) + (filters.search ? 1 : 0)
        + (filters.owner !== 'any' ? 1 : 0) + (filters.bank !== 'any' ? 1 : 0)
        + (filters.from ? 1 : 0) + (filters.to ? 1 : 0) + (filters.customer ? 1 : 0);

    const act = async (row: ChequeRow, status: ChequeStatus, label: string) => {
        if (busyId) return;
        setBusyId(row.id);
        setNotice(null);
        try {
            await changeChequeStatus(workspace.id, row.id, status, row.updatedAt);
            await reload();
            setNotice({ tone: 'ok', text: `${row.chequeNumber || 'That cheque'} — ${label.toLowerCase()}.` });
        } catch (failed) {
            setNotice({ tone: 'error', text: failed instanceof Error ? failed.message : 'That did not work.' });
            // A conflict means this screen is out of date. Reading it again is
            // the only honest thing to show next.
            await reload();
        } finally {
            setBusyId(null);
        }
    };

    return (
        <Page width="wide">
            <PageHeader
                title="Cheques"
                subtitle="Every cheque you hold, and what needs doing with it."
                actions={canManage ? (
                    <Button variant="primary" onClick={() => setEditing('new')}>Record a cheque</Button>
                ) : undefined}
            />

            {notice && <div className="mb-4"><FormNotice tone={notice.tone}>{notice.text}</FormNotice></div>}
            {error && <div className="mb-4"><FormNotice tone="error">{error}</FormNotice></div>}

            <Card className="overflow-hidden">
                <StateStrip
                    totals={totals}
                    active={filters.state}
                    onPick={state => set({ state: state === filters.state ? 'any' : state })}
                />

                <Filters
                    filters={filters}
                    banks={banks}
                    owners={wholeBook ? [...names].map(([id, name]) => ({ id, name })) : null}
                    today={today}
                    narrowed={narrowed}
                    onChange={set}
                    onClear={() => setParams(new URLSearchParams(), { replace: true })}
                />

                {filters.customer && (
                    <div className="px-4 py-2 border-b border-separator flex items-center gap-2 flex-wrap text-secondary bg-accent-tint">
                        <span className="text-label">
                            Only the cheques from <strong className="font-semibold">{customerName ?? 'one account'}</strong>
                        </span>
                        <Button size="sm" variant="link" onClick={() => set({ customer: '' })}>Show every cheque</Button>
                    </div>
                )}

                {canManage && chosen.size > 0 && (
                    <SelectionBar
                        count={chosen.size}
                        noun="cheque"
                        total={Math.min(totals?.matchedCount ?? rows.length, CHEQUE_BULK_LIMIT)}
                        selectAllLabel={choosingAll ? 'Choosing…'
                            : (totals?.matchedCount ?? 0) > CHEQUE_BULK_LIMIT
                                ? `Choose the first ${count(CHEQUE_BULK_LIMIT)} of ${count(totals?.matchedCount ?? 0)}`
                                : `Choose all ${count(totals?.matchedCount ?? rows.length)}`}
                        onSelectAll={() => { void chooseAll(); }}
                        onClear={() => setChosen(new Set())}
                    >
                        {BULK_MOVES.map(move => (
                            <Button key={move.status} size="sm" variant="secondary"
                                onClick={() => setMoving({ status: move.status, ids: [...chosen] })}>
                                {move.label}
                            </Button>
                        ))}
                    </SelectionBar>
                )}
                {totals && (
                    <div className="px-4 py-2 border-b border-separator flex items-baseline gap-2 flex-wrap text-caption text-label-3">
                        <strong className="text-body font-semibold text-label num">{money.exact(totals.matchedAmount)}</strong>
                        <span>
                            across {count(totals.matchedCount)}{' '}
                            {totals.matchedCount === 1 ? 'cheque' : 'cheques'}
                            {totals.matchedCount !== totals.visibleCount && <> of {count(totals.visibleCount)}</>}
                        </span>
                        {rows.length < totals.matchedCount && <span>· showing {count(rows.length)}</span>}
                    </div>
                )}

                {loading ? (
                    <LoadingList label="Loading your cheques" rows={6} />
                ) : error ? (
                    <div className="px-5 py-6">
                        <Button variant="secondary" onClick={() => { void reload(); }}>Try again</Button>
                    </div>
                ) : totals !== null && totals.visibleCount === 0 ? (
                    <EmptyState
                        title="No cheques yet"
                        hint={canManage
                            ? 'Record the first cheque somebody has given you, and it appears here with its date and what needs doing.'
                            : 'Once somebody records a cheque, it appears here.'}
                        action={canManage
                            ? <Button variant="primary" onClick={() => setEditing('new')}>Record your first cheque</Button>
                            : undefined}
                    />
                ) : rows.length === 0 ? (
                    <EmptyState
                        title="Nothing matches"
                        hint={totals
                            ? `None of your ${count(totals.visibleCount)} cheques match what you are looking for.`
                            : 'No cheque matches what you are looking for.'}
                        action={
                            <Button variant="secondary" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
                                Clear filters
                            </Button>
                        }
                    />
                ) : asCards ? (
                    <ul className="divide-y divide-separator">
                        {rows.map(row => (
                            <ChequeCard
                                key={row.id} row={row} today={today} slug={workspace.slug}
                                canManage={canManage} busy={busyId === row.id}
                                onAct={act} onEdit={() => setEditing(row)}
                                onDelete={() => setConfirmDelete(row)}
                                chosen={chosen.has(row.id)} onChoose={canManage ? on => toggle(row.id, on) : undefined}
                            />
                        ))}
                    </ul>
                ) : (
                    <Table caption="Cheques in the register, with their state and what can be done next." className="table-fixed">
                        <thead>
                            <tr>
                                {canManage && (
                                    <Th className="w-11 !pr-0">
                                        <input
                                            type="checkbox"
                                            aria-label={`Choose all ${rows.length} cheques shown`}
                                            checked={rows.length > 0 && rows.every(r => chosen.has(r.id))}
                                            onChange={e => setChosen(current => {
                                                const next = new Set(current);
                                                for (const r of rows) { if (e.target.checked) next.add(r.id); else next.delete(r.id); }
                                                return next;
                                            })}
                                        />
                                    </Th>
                                )}
                                <Th>Customer</Th>
                                <Th className="w-32">Cheque</Th>
                                <Th hideBelow="2xl" className="w-44">Bank</Th>
                                <Th className="w-32">Dated</Th>
                                <Th numeric className="w-32">Amount</Th>
                                <Th className="w-32">State</Th>
                                <Th className={canManage ? 'text-right w-64' : 'w-4'}>{canManage ? <span>What next</span> : <span className="sr-only">Actions</span>}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <ChequeTableRow
                                    key={row.id} row={row} today={today} slug={workspace.slug}
                                    canManage={canManage} busy={busyId === row.id}
                                    onAct={act} onEdit={() => setEditing(row)}
                                    onDelete={() => setConfirmDelete(row)}
                                    chosen={chosen.has(row.id)} onChoose={canManage ? on => toggle(row.id, on) : undefined}
                                />
                            ))}
                        </tbody>
                    </Table>
                )}

                {hasMore && !loading && (
                    <div className="px-4 py-3 border-t border-separator flex justify-center">
                        <Button variant="secondary" disabled={loadingMore} onClick={() => { void loadMore(); }} busy={loadingMore}>
                            {loadingMore ? 'Loading…' : totals
                                ? `Load more (${count(rows.length)} of ${count(totals.matchedCount)})`
                                : `Load more (${count(rows.length)} so far)`}
                        </Button>
                    </div>
                )}

            </Card>

            {moving && (
                <ChequeBulkDialog
                    workspaceId={workspace.id}
                    ids={moving.ids}
                    status={moving.status}
                    onClose={() => setMoving(null)}
                    onDone={async message => {
                        setMoving(null);
                        setChosen(new Set());
                        await reload();
                        setNotice({ tone: 'ok', text: message });
                    }}
                />
            )}

            {editing && (
                <ChequeDialog
                    cheque={editing === 'new' ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={async message => {
                        setEditing(null);
                        await reload();
                        setNotice({ tone: 'ok', text: message });
                    }}
                />
            )}

            <ConfirmDialog
                open={confirmDelete !== null}
                title="Delete this cheque?"
                confirmLabel="Delete it"
                onCancel={() => setConfirmDelete(null)}
                onConfirm={() => {
                    const row = confirmDelete;
                    setConfirmDelete(null);
                    if (!row) return;
                    void (async () => {
                        try {
                            await deleteCheque(workspace.id, row.id);
                            await reload();
                            setNotice({ tone: 'ok', text: 'That cheque has been deleted.' });
                        } catch (failed) {
                            setNotice({
                                tone: 'error',
                                text: failed instanceof Error ? failed.message : 'That did not work.',
                            });
                        }
                    })();
                }}
            >
                <p>
                    {confirmDelete?.chequeNumber
                        ? `Cheque ${confirmDelete.chequeNumber}`
                        : 'This cheque'}
                    {confirmDelete ? ` for ${money.exact(confirmDelete.amount)}` : ''}
                    {confirmDelete?.customerName ? `, from ${confirmDelete.customerName}` : ''}.
                    {' '}Deleting it removes it from the register and from every total.
                    If it bounced or was cancelled, say so instead — that keeps the history.
                </p>
            </ConfirmDialog>

            {/* The register's own dates are the company's, not the browser's. */}
            <p className="sr-only">Dates are shown in {date.zoneLabel}.</p>
        </Page>
    );
}

/* -------------------------------- the states ------------------------------ */

const STATE_TONE = {
    dang: 'text-dang', warn: 'text-warn', brand: 'text-accent-text',
    pos: 'text-pos', neutral: 'text-label',
} as const;

/**
 * The six states: the overview and the filter in one control.
 *
 * Six figures in a row with nothing drawn between them (2026-10 global sweep: the hairline grid made six boxes
 * of it), and the chosen state marked by one line that slides to it — the tabs' mark — rather than a fill that
 * switches from one box to the next. Each state is a real button with `aria-pressed`, so a keyboard reaches them
 * in order and a screen reader says which one is on. Pressing the active state again clears it, which is what
 * people try first. Two rows of three on a phone: the mark moves between rows too.
 */
function StateStrip({
    totals, active, onPick,
}: {
    totals: ChequeTotals | null;
    active: ChequeState | 'any';
    onPick: (state: ChequeState) => void;
}) {
    const { money, count } = useFormatters();
    const { track, box, animate } = useIndicator<HTMLDivElement>(active);

    return (
        <div ref={track} className="relative grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 border-b border-separator">
            {box && (
                <span aria-hidden="true" data-indicator=""
                    style={{ transform: `translate(${box.left}px, ${box.top + box.height - 2}px)`, width: box.width }}
                    className={cx('absolute left-0 top-0 h-0.5 bg-accent', animate && 'transition-[transform,width] duration-200 ease-out-quart')} />
            )}
            {CHEQUE_STATE_ORDER.map(state => {
                const figure = totals?.byState[state] ?? { count: 0, amount: 0 };
                const on = active === state;
                return (
                    <button
                        key={state}
                        type="button"
                        aria-pressed={on}
                        data-active={on}
                        title={CHEQUE_STATES[state].hint}
                        onClick={() => onPick(state)}
                        className={cx(
                            'text-left px-4 py-3 min-h-16 transition-colors duration-150 hover:bg-hover',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
                        )}
                    >
                        <div className="flex items-baseline justify-between gap-2">
                            <span className={cx('text-secondary font-semibold truncate transition-colors duration-150', on ? 'text-accent-text' : 'text-label-2')}>
                                {CHEQUE_STATES[state].label}
                            </span>
                            <span className={cx(
                                'text-heading font-semibold num leading-none',
                                figure.count === 0 ? 'text-label-4' : STATE_TONE[CHEQUE_STATES[state].tone],
                            )}>
                                {count(figure.count)}
                            </span>
                        </div>
                        <div className="text-caption text-label-3 num mt-1.5">
                            {figure.count === 0 ? '—' : money.compact(figure.amount)}
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

/* ------------------------------- the filters ------------------------------ */

function Filters({
    filters, banks, owners, today, narrowed, onChange, onClear,
}: {
    filters: ChequeFilters;
    banks: { bankName: string; chequeCount: number }[];
    /** Everybody's name, for somebody who sees the whole book; null for everyone else. */
    owners: { id: string; name: string }[] | null;
    today: Date;
    narrowed: number;
    onChange: (changes: Partial<ChequeFilters>) => void;
    onClear: () => void;
}) {
    const preset = filters.from && filters.to ? chequeDatePresetOf({ from: filters.from, to: filters.to }, today) : null;
    const handMade = Boolean((filters.from || filters.to) && !preset);
    const [open, setOpen] = useState(handMade || filters.bank !== 'any' || filters.owner !== 'any');
    const folded = (filters.bank !== 'any' ? 1 : 0) + (filters.owner !== 'any' ? 1 : 0) + (handMade ? 1 : 0);

    return (
        <div className="border-b border-separator">
            <div className="px-4 py-3 flex items-end gap-2.5 flex-wrap">
                <div className="flex-1 min-w-48 md:max-w-sm">
                    <label htmlFor="cheque-search" className={filterLabelClass}>Search</label>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-label-3 pointer-events-none"><IconSearch /></span>
                        <input
                            id="cheque-search"
                            type="search"
                            data-shortcut-search
                            aria-keyshortcuts="/"
                            value={filters.search}
                            placeholder="Customer, cheque number or bank"
                            onChange={e => onChange({ search: e.target.value })}
                            className={fieldClass({ className: cx('w-full pl-9', filters.search && '!border-accent bg-accent-tint') })}
                        />
                    </div>
                </div>

                {/* One press for the ranges people reach for; the same two dates, set for them. */}
                <div role="group" aria-label="Dated: quick choices" className="flex items-center gap-1.5 flex-wrap pb-0.5">
                    {CHEQUE_DATE_PRESETS.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            aria-pressed={preset === option.value}
                            onClick={() => onChange(preset === option.value
                                ? { from: '', to: '' }
                                : chequeDateRange(option.value, today))}
                            className={chipClass(preset === option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>

                <Button
                    variant="quiet"
                    aria-expanded={open}
                    aria-controls="cheque-more-filters"
                    onClick={() => setOpen(v => !v)}
                >
                    {open ? 'Fewer filters' : folded > 0 ? <>More filters <span className="num">({folded})</span></> : 'More filters'}
                </Button>

                {narrowed > 0 && (
                    <Button variant="quiet" onClick={onClear}>Show all</Button>
                )}
            </div>

            <div id="cheque-more-filters" hidden={!open} className="px-4 py-3 border-t border-separator bg-card-2">
                <div className="flex items-end gap-2.5 flex-wrap">
                    {owners && (
                        <div>
                            <label htmlFor="cheque-owner" className={filterLabelClass}>Owner</label>
                            <select
                                id="cheque-owner"
                                value={filters.owner}
                                onChange={e => onChange({ owner: e.target.value })}
                                className={fieldClass({ className: cx('min-w-40', filters.owner !== 'any' && '!border-accent bg-accent-tint') })}
                            >
                                <option value="any">Anyone</option>
                                <option value="mine">Me</option>
                                <option value="unassigned">Nobody</option>
                                {owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                            </select>
                        </div>
                    )}
                    <div>
                        <label htmlFor="cheque-bank" className={filterLabelClass}>Bank</label>
                        <select
                            id="cheque-bank"
                            value={filters.bank}
                            onChange={e => onChange({ bank: e.target.value })}
                            className={fieldClass({ className: cx('min-w-40', filters.bank !== 'any' && '!border-accent bg-accent-tint') })}
                        >
                            <option value="any">Any bank</option>
                            {banks.map(b => (
                                <option key={b.bankName} value={b.bankName}>
                                    {b.bankName} ({b.chequeCount})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="w-48">
                        <DateField id="cheque-from" label="Dated from" value={filters.from} clearable
                            onChange={value => onChange({ from: value })} />
                    </div>
                    <div className="w-48">
                        <DateField id="cheque-to" label="Dated to" value={filters.to} clearable
                            notBefore={filters.from || undefined}
                            onChange={value => onChange({ to: value })} />
                    </div>
                </div>
            </div>
        </div>
    );
}

/* -------------------------------- the rows -------------------------------- */

const BADGE_TONE = {
    dang: 'dang', warn: 'warn', brand: 'brand', pos: 'pos', neutral: 'neutral',
} as const;

interface RowProps {
    row: ChequeRow;
    today: Date;
    slug: string;
    canManage: boolean;
    busy: boolean;
    onAct: (row: ChequeRow, status: ChequeStatus, label: string) => void | Promise<void>;
    onEdit: () => void;
    onDelete: () => void;
    chosen: boolean;
    /** Present when cheques can be chosen for a change together. */
    onChoose: ((chosen: boolean) => void) | undefined;
}

/** Below this the register is cards: the table's columns and actions no longer fit beside the sidebar (DESIGN-SYSTEM §9). */
const CARDS_QUERY = '(max-width: 1279px)';

function ChequeTableRow({ row, today, slug, canManage, busy, onAct, onEdit, onDelete, chosen, onChoose }: RowProps) {
    const { money, date } = useFormatters();
    const state = stateOf(row, today);

    return (
        <tr className={cx('hover:bg-hover', busy && 'opacity-60', chosen && 'bg-accent-tint')}>
            {onChoose && (
                <Td className="!pr-0">
                    <input
                        type="checkbox"
                        aria-label={`Choose cheque ${row.chequeNumber || 'with no number'}${row.customerName ? ` from ${row.customerName}` : ''}`}
                        checked={chosen}
                        onChange={e => onChoose(e.target.checked)}
                    />
                </Td>
            )}
            <Td className="font-semibold truncate">
                {row.customerId ? (
                    <Link to={paths(slug).customer(row.customerId)} className="text-label hover:text-accent-text hover:underline">
                        {row.customerName || 'That account'}
                    </Link>
                ) : (
                    <span className="text-label-2 font-normal">{row.customerName || 'Not linked'}</span>
                )}
            </Td>
            <Td className="text-label-2 num whitespace-nowrap truncate">{row.chequeNumber || '—'}</Td>
            <Td hideBelow="2xl" className="text-label-2 truncate">{row.bankName || '—'}</Td>
            <Td className="text-label-2 num whitespace-nowrap">{row.chequeDate ? date.day(row.chequeDate) : '—'}</Td>
            <Td numeric className="text-label font-semibold">{money.exact(row.amount)}</Td>
            <Td>
                <Badge variant="text" dot tone={BADGE_TONE[CHEQUE_STATES[state].tone]} title={CHEQUE_STATES[state].hint}>
                    {CHEQUE_STATES[state].label}
                </Badge>
            </Td>
            <Td className="text-right">
                {/* What the state allows, as quiet words that are each one press; correcting or removing the record
                    is not the day's work, so Edit and Delete wait behind one "More" (2026-10 global sweep: five
                    outlined buttons on every row). */}
                {canManage && (
                    <div className="flex items-center gap-0.5 justify-end">
                        {chequeActions(state).map(action => (
                            <Button
                                key={action.label}
                                size="sm"
                                variant="row"
                                title={action.title}
                                disabled={busy}
                                onClick={() => { void onAct(row, action.to, action.label); }}
                            >
                                {action.label}
                            </Button>
                        ))}
                        <MenuButton
                            label={<IconMore size={18} />}
                            name={`More for cheque ${row.chequeNumber || 'with no number'}`}
                            variant="quiet"
                            size="sm"
                            buttonClassName="!w-8 !px-0"
                            items={[
                                { label: 'Edit', onSelect: onEdit },
                                { label: 'Delete', onSelect: onDelete, tone: 'dang' },
                            ]}
                        />
                    </div>
                )}
            </Td>
        </tr>
    );
}

/**
 * One cheque where the table does not fit: a phone, a tablet, a small laptop.
 *
 * A card rather than a squeezed row: six columns at 360px is six unreadable
 * columns. Every action stays visible and at least 40px tall — nothing hides
 * behind hover, which does not exist here, or behind an overflow menu, which
 * makes the ordinary job take two presses.
 */
function ChequeCard({ row, today, slug, canManage, busy, onAct, onEdit, onDelete, chosen, onChoose }: RowProps) {
    const { money, date } = useFormatters();
    const state = stateOf(row, today);

    return (
        <li className={cx('px-4 py-3', busy && 'opacity-60', chosen && 'bg-accent-tint')}>
            <div className="flex items-start justify-between gap-3">
                {onChoose && (
                    <label className="flex-none inline-flex items-center justify-center h-10 w-10 -ml-2.5 -mt-2 cursor-pointer">
                        <input
                            type="checkbox"
                            aria-label={`Choose cheque ${row.chequeNumber || 'with no number'}${row.customerName ? ` from ${row.customerName}` : ''}`}
                            checked={chosen}
                            onChange={e => onChoose(e.target.checked)}
                        />
                    </label>
                )}
                <div className="min-w-0 flex-1">
                    {row.customerId ? (
                        <Link
                            to={paths(slug).customer(row.customerId)}
                            className="text-body font-semibold text-label hover:text-accent-text hover:underline block truncate"
                        >
                            {row.customerName || 'That account'}
                        </Link>
                    ) : (
                        <span className="text-body font-semibold text-label-2 block truncate">
                            {row.customerName || 'Not linked'}
                        </span>
                    )}
                    <div className="text-caption text-label-3 mt-0.5 num">
                        {row.chequeNumber || 'No number'}
                        {row.bankName ? ` · ${row.bankName}` : ''}
                    </div>
                </div>
                <div className="text-right flex-none">
                    <div className="text-body font-semibold text-label num">{money.exact(row.amount)}</div>
                    <div className="text-caption text-label-3 num">{row.chequeDate ? date.day(row.chequeDate) : 'Undated'}</div>
                </div>
            </div>

            <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                <Badge variant="text" dot tone={BADGE_TONE[CHEQUE_STATES[state].tone]}>{CHEQUE_STATES[state].label}</Badge>
                <span className="text-caption text-label-3">{CHEQUE_STATES[state].hint}</span>
            </div>

            {/* Two rows by design rather than whatever wrapping leaves (a lone Delete under every card): what the state
                allows, sharing the width; then correcting or removing the record, quieter. */}
            {canManage && (
                <div className="mt-3 grid gap-1">
                    <div className="grid grid-flow-col auto-cols-fr gap-2">
                        {chequeActions(state).map(action => (
                            <Button
                                key={action.label}
                                size="sm"
                                variant="secondary"
                                title={action.title}
                                disabled={busy}
                                className="min-h-10"
                                onClick={() => { void onAct(row, action.to, action.label); }}
                            >
                                {action.label}
                            </Button>
                        ))}
                    </div>
                    <div className="flex items-center gap-1 -ml-3">
                        <Button size="sm" variant="quiet" className="min-h-10" onClick={onEdit}>Edit</Button>
                        <Button size="sm" variant="quiet" className="min-h-10" onClick={onDelete}>Delete</Button>
                    </div>
                </div>
            )}
        </li>
    );
}

/* -------------------------------------------------------------------------- */

/**
 * The filters, read out of the address bar.
 *
 * So a filtered register can be linked to — Today's "3 bounced" opens exactly
 * that — and a back press from an account returns to the list as it was.
 * Anything unrecognised falls back to the default rather than being trusted.
 */
function filtersFromParams(params: URLSearchParams): ChequeFilters {
    const state = params.get('state');
    const day = (value: string | null): string =>
        value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';

    const customer = params.get('customer') ?? '';
    return {
        state: isChequeState(state) ? state : 'any',
        search: params.get('search') ?? '',
        owner: params.get('owner') ?? DEFAULT_CHEQUE_FILTERS.owner,
        bank: params.get('bank') ?? DEFAULT_CHEQUE_FILTERS.bank,
        from: day(params.get('from')),
        to: day(params.get('to')),
        // An account id, or nothing: anything else is dropped rather than trusted.
        customer: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(customer) ? customer : '',
    };
}

/** The name of the account the register is narrowed to, read under the caller's own scope. */
function useCustomerName(workspaceId: string, customerId: string): string | null {
    const [name, setName] = useState<string | null>(null);
    useEffect(() => {
        if (!customerId) { setName(null); return; }
        let current = true;
        void customerById(workspaceId, customerId).then(found => { if (current) setName(found?.company ?? null); });
        return () => { current = false; };
    }, [workspaceId, customerId]);
    return name;
}
